import { useState, useEffect, useMemo } from 'react';
import { MapPin, X, Shield, LayoutList } from 'lucide-react';
import useProjectStore from '../stores/useProjectStore';
import useAreaStore from '../stores/useAreaStore';
import usePropertyStore from '../stores/usePropertyStore';
import useUserStore from '../stores/useUserStore';
import UniversalIconPicker from './ui/UniversalIconPicker';
import ColorPicker from './ui/ColorPicker';
import CustomSelect from './ui/CustomSelect';
import PropertyField from './properties/PropertyField';
import { getProjectStatuses } from '../constants/systemProperties';
import { useNavigate } from 'react-router-dom';
import './ProjectModal.css';

export default function ProjectModal({ project, onClose }) {
    const addProject = useProjectStore(s => s.addProject);
    const updateProject = useProjectStore(s => s.updateProject);
    const areas = useAreaStore(s => s.areas);
    const globalProperties = usePropertyStore(s => s.properties);
    const preferences = useUserStore(s => s.preferences);
    const navigate = useNavigate();

    const [formName, setFormName] = useState('');
    const [formDesc, setFormDesc] = useState('');
    const [formColor, setFormColor] = useState('categorical-6');
    const [formIcon, setFormIcon] = useState({ type: 'emoji', value: '📁', color: null });
    const [formAreaId, setFormAreaId] = useState('');
    const [formStatus, setFormStatus] = useState('planned');
    const [formProperties, setFormProperties] = useState({});

    // Dynamic project statuses from constants
    const statusOptions = useMemo(() => {
        return getProjectStatuses(preferences).map(s => ({
            value: s.key,
            label: s.label,
            color: s.color,
            emoji: s.emoji || ''
        }));
    }, [preferences]);

    useEffect(() => {
        if (project) {
            setFormName(project.name || '');
            setFormDesc(project.description || '');
            setFormColor(project.color || 'categorical-6');
            setFormIcon(project.icon || { type: 'emoji', value: project.emoji || '📁', color: null });
            setFormAreaId(project.areaId || '');
            setFormStatus(project.status || 'planned');
            setFormProperties(project.properties || {});
        } else {
            setFormName('');
            setFormDesc('');
            setFormColor('categorical-6');
            setFormIcon({ type: 'emoji', value: '📁', color: null });
            setFormAreaId('');
            setFormStatus('planned');
            setFormProperties({});
        }
    }, [project]);

    const handleSave = () => {
        if (!formName.trim()) return;
        const data = { 
            name: formName.trim(), 
            description: formDesc.trim(), 
            color: formColor, 
            icon: formIcon, 
            emoji: formIcon.type === 'emoji' ? formIcon.value : '📁', 
            areaId: formAreaId || null,
            status: formStatus,
            properties: formProperties
        };
        
        if (project?.id) {
            updateProject(project.id, data);
            onClose();
        } else {
            const newProj = addProject(data);
            onClose();
            navigate(`/projects/${newProj.id}`);
        }
    };

    const handlePropertyChange = (propId, value) => {
        setFormProperties(prev => ({
            ...prev,
            [propId]: value
        }));
    };

    return (
        <div className="project-modal-overlay" onClick={onClose}>
            <div className="project-modal animate-scale-in" onClick={e => e.stopPropagation()}>
                <div className="project-modal-header">
                    <h2>{project ? 'Edit Project' : 'New Project'}</h2>
                    <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
                </div>
                
                <div className="project-modal-body">
                    {/* Basic Info grouped visually */}
                    <div className="pm-form-group">
                        <label className="input-label">Name</label>
                        <input className="input-field" value={formName} onChange={e => setFormName(e.target.value)} placeholder="Project name" autoFocus />
                    </div>
                    
                    <div className="pm-form-group">
                        <label className="input-label">Description</label>
                        <textarea className="input-field" value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="What is this project about?" rows={2} />
                    </div>
                    
                    {/* Status & Area grid */}
                    <div className="pm-form-grid">
                        <div className="pm-form-group">
                            <label className="input-label"><Shield size={14} /> Status</label>
                            <CustomSelect
                                value={formStatus}
                                onChange={setFormStatus}
                                options={statusOptions}
                                placeholder="Select status..."
                                searchable={false}
                            />
                        </div>
                        <div className="pm-form-group">
                            <label className="input-label"><MapPin size={14} /> Area</label>
                            <CustomSelect
                                value={formAreaId}
                                onChange={setFormAreaId}
                                options={[
                                    { value: '', label: 'No area' },
                                    ...areas.map(a => ({ value: a.id, label: a.name, emoji: a.emoji })),
                                ]}
                                placeholder="Select area..."
                            />
                        </div>
                    </div>

                    {/* Styling grid */}
                    <div className="pm-form-grid">
                        <div className="pm-form-group">
                            <label className="input-label">Icon</label>
                            <UniversalIconPicker value={formIcon} onChange={setFormIcon} />
                        </div>
                        <div className="pm-form-group">
                            <label className="input-label">Color</label>
                            <ColorPicker value={formColor} onChange={setFormColor} />
                        </div>
                    </div>

                    {/* Properties Section */}
                    {globalProperties.length > 0 && (
                        <>
                            <div className="pm-section-header">
                                <LayoutList size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 'var(--space-xxs)' }}/>
                                Custom Properties
                            </div>
                            <div className="pm-form-grid">
                                {globalProperties.map(prop => (
                                    <div key={prop.id} className="pm-form-group">
                                        <label className="input-label">{prop.name}</label>
                                        <PropertyField
                                            property={prop}
                                            value={formProperties[prop.id]}
                                            onChange={(val) => handlePropertyChange(prop.id, val)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                
                <div className="project-modal-footer">
                    <div className="project-modal-footer__left">
                        {/* Space for future left actions, e.g. Delete */}
                    </div>
                    <div className="project-modal-footer__right">
                        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleSave} disabled={!formName.trim()}>
                            {project ? 'Save Changes' : 'Create Project'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
